import React, { useCallback, useContext, useMemo, useState } from 'react'

import { useTranslations } from 'next-intl'

import { useDeepCompareEffect } from 'ahooks'

import classNames from 'classnames'

import AvailabilityIndicator from '~/components/atoms/AvailabilityIndicator/AvailabilityIndicator'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import { Icon } from '~/components/atoms/Icon/Icon'
import PortfolioCtas from '~/components/atoms/PortfolioCtas/PortfolioCtas'
import PortfolioItemCard from '~/components/atoms/PortfolioItemCard/PortfolioItemCard'
import PortfolioTableArtwork from '~/components/atoms/PortfolioTableArtwork/PortfolioTableArtwork'
import type { ExpandableColumn } from '~/components/atoms/Table/Table'
import Table from '~/components/atoms/Table/Table'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import TicketLozenges, {
  TicketLozengeSizes,
} from '~/components/atoms/TicketLozenges/TicketLozenges'
import { modalTypes } from '~/components/modalLoader'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { salesValuationTypes } from '~/enums/hsSalesValuationStatus'
import { valuationStatusTypes } from '~/enums/hsValuationStatus'
import useInventoryParam from '~/hooks/useInventoryParam'
import useGeoLocation from '~/hooks/useLocation'
import usePortfolioInfoWindow from '~/hooks/usePortfolioInfoWindow'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import analyticsPusher, { analyticsEventTypes } from '~/utils/analyticsPusher'
import { getArtworkPrice } from '~/utils/getArtworkPrice'
import { renderGuidePrice, renderMarketValue } from '~/utils/portfolioRenderers'

import EmptyGrowthMessage from '../EmptyGrowthMessage/EmptyGrowthMessage'
import GrowthStatus from '../GrowthStatus/GrowthStatus'
import type { PortfolioCardItemProps } from '../PortfolioCard/PortfolioCard'
import PortfolioCard from '../PortfolioCard/PortfolioCard'
import { PortfolioCollectionVariant } from '../PortfolioCollection/PortfolioCollection'
import PortfolioItemDelete from '../PortfolioCollectionTableMenu/PortfolioItemDelete'

import styles from './PortfolioCollectionTable.module.css'

export interface PortfolioCollectionTableProps {
  testID?: string
  items: PortfolioCardItemProps[]
  variant: PortfolioCollectionVariant
  isTableView?: boolean
}

interface TableViewProps {
  items: PortfolioCardItemProps[]
  variant: PortfolioCollectionVariant
  isTableView?: boolean
}

type Listing = {
  artworkDetails: React.ReactElement
  purchasePrice?: JSX.Element | number
  growthSinceOwned?: JSX.Element
  marketValue?: JSX.Element | number
  guidePrice?: JSX.Element
  growth5Year?: JSX.Element
  saleStatus?: JSX.Element
  inventoryPriority?: number
  purchaseStatus?: any
  salesValuation?: number | string
  cta?: any
  menu: any
  hasHistory?: any
}

const TableView = ({ items, variant, isTableView = true }: TableViewProps) => {
  const tCommon = useTranslations('common')
  const t = useTranslations('dashboard')
  const isMobile = useMediaQuery(`(max-width: ${bp.bpLargeMax})`)
  const { openInfoWindow } = usePortfolioInfoWindow()
  const { stateDispatch } = useContext(GlobalStoreContext)
  const [artistOrder, setArtistOrder] = useState<'asc' | 'desc'>('asc')
  const [deletedItemIndex, setDeletedItemIndex] = useState<number | null>(null)
  const { activeCurrencyIso } = useGeoLocation()

  useDeepCompareEffect(() => {
    setDeletedItemIndex(null)
  }, [items])

  const sortByArtist = useCallback(
    (a: any, b: any) => {
      // Get artist names and titles
      const artistA = (a?.artwork?.artist || '').toLowerCase().trim()
      const artistB = (b?.artwork?.artist || '').toLowerCase().trim()
      const titleA = (a?.artwork?.title || '').toLowerCase().trim()
      const titleB = (b?.artwork?.title || '').toLowerCase().trim()

      if (artistA !== artistB) {
        return artistOrder === 'asc'
          ? artistA.localeCompare(artistB)
          : artistB.localeCompare(artistA)
      }

      return artistOrder === 'asc'
        ? titleA.localeCompare(titleB)
        : titleB.localeCompare(titleA)
    },
    [artistOrder]
  )

  // Handler for clicking the header
  const handleArtworkSort = useCallback(() => {
    setArtistOrder(current => (current === 'asc' ? 'desc' : 'asc'))
  }, [])

  useInventoryParam(items, variant)

  const openPriceAndDateModal = ({ ticketId, artist, artwork }: any) => {
    stateDispatch({
      type: GlobalStoreProps.UPDATE_MODAL_STATE,
      payload: {
        isOpen: true,
        component: modalTypes.addPriceAndDateModal,
        content: null,
        reloadData: false,
        props: {
          ticketId: ticketId,
          artist: artist,
          artwork: artwork,
          isReducedWidth: true,
          modifierClass: 'portfolioArtwork',
          reloadData: false,
        },
      },
    })
  }

  const columns: ExpandableColumn<Listing>[] = useMemo(() => {
    const growth: ExpandableColumn<Listing> = {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          {t('growthAAGR')} <br />
          {t('fiveYr')}
        </Text>
      ),
      maxWidth: 80,
      accessor: 'growth5Year',
      isExpandable: true,
      expandableText: <EmptyGrowthMessage />,
    }

    const marketValue: ExpandableColumn<Listing> = {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          {t('market')} <br /> {t('value')}
        </Text>
      ),
      maxWidth: 100,
      accessor: 'marketValue',
    }

    const cols = [
      {
        Header: (
          <Text uppercase size={TextSizes.bodySml}>
            {t('artwork')} <br />
            {t('details')}
          </Text>
        ),
        minWidth: 150,
        accessor: 'artworkDetails',
        isSortable: true,

        sortProps: {
          isSorted: Boolean(artistOrder),
          isSortedDesc: artistOrder === 'desc',
        },
      },
    ] as ExpandableColumn<Listing>[]

    switch (variant) {
      case PortfolioCollectionVariant.Myportfolio:
        cols.push(marketValue, growth, {
          Header: (
            <Text uppercase size={TextSizes.bodySml}>
              {t('growth')} <br /> {t('sincePurchase')}
            </Text>
          ),
          maxWidth: 80,
          accessor: 'growthSinceOwned',
        })
        break
      case PortfolioCollectionVariant.Selling:
        cols.push(
          {
            Header: (
              <Text uppercase size={TextSizes.bodySml}>
                {t('sales')}
                <br /> {t('valuation')}
              </Text>
            ),
            maxWidth: 100,
            accessor: 'salesValuation',
          },
          {
            Header: (
              <Text uppercase size={TextSizes.bodySml}>
                {t('auction')} <br /> {t('data')}
              </Text>
            ),
            maxWidth: 80,
            accessor: 'hasHistory',
          },
          {
            Header: (
              <Text uppercase size={TextSizes.bodySml}>
                {t('sale')} <br /> {t('status')}
              </Text>
            ),
            accessor: 'saleStatus',
            maxWidth: !isMobile ? 100 : 80,
          }
        )
        break
      case PortfolioCollectionVariant.Watching:
        cols.push(marketValue, growth, {
          Header: (
            <Text uppercase size={TextSizes.bodySml}>
              {t('availability')}
            </Text>
          ),
          maxWidth: 100,
          accessor: 'inventoryPriority',
        })
        break
      case PortfolioCollectionVariant.Buying:
        cols.push(
          {
            Header: (
              <Text uppercase size={TextSizes.bodySml}>
                {t('guidePrice')}
              </Text>
            ),
            maxWidth: 100,
            accessor: 'guidePrice',
          },
          growth,
          {
            Header: (
              <Text uppercase size={TextSizes.bodySml}>
                {tCommon('purchase')}
                <br /> {t('status')}
              </Text>
            ),
            maxWidth: 100,
            accessor: 'purchaseStatus',
          }
        )
        break
    }

    // Final two standard columns for all variants
    cols.push({
      Header: '',
      accessor: 'cta',
      maxWidth: 100,
    })

    return cols
  }, [artistOrder, isMobile, variant])

  const sortedData = useMemo(() => {
    if (artistOrder === null) return items

    return [...items].sort(sortByArtist)
  }, [items, sortByArtist, artistOrder])

  // Processed data
  const data =
    sortedData &&
    sortedData.map((item, index) => {
      if (!item.artwork || index === deletedItemIndex) {
        return {}
      }

      return {
        artworkDetails: (
          <PortfolioTableArtwork
            item={item}
            hasHistory={item.hasHistory}
            linkArtwork={!item.artwork.agnostic}
            useInfoDrawer
            variant={variant}
          />
        ),
        growthSinceOwned: (
          <div className={styles.cell}>
            {item.growthSinceOwned ? (
              <div
                className={classNames(
                  styles.cell,
                  styles.actionable,
                  styles.auctionData
                )}
                onClick={() => openInfoWindow(item, variant)}
              >
                <GrowthStatus size="medium" growth={+item.growthSinceOwned} />
              </div>
            ) : (
              <>
                {item.hasHistory ? (
                  <Button
                    button={{
                      type: ButtonTypes.button,
                      onClick: () => {
                        analyticsPusher(analyticsEventTypes.gaEvent, {
                          gaEventName: 'Calculate button pressed',
                        })
                        openPriceAndDateModal({
                          ticketId: item.ticketId,
                          artwork: item.artwork.title,
                          artist: item.artwork.artist,
                        })
                      },
                    }}
                    variant={ButtonVariants.textLinkSmall}
                    underline
                  >
                    {t('calculate')}
                  </Button>
                ) : (
                  <Text size={TextSizes.bodySml}>{t('notAvailable')}</Text>
                )}
              </>
            )}
          </div>
        ),
        salesValuation: item.salesValuation && (
          <div className={styles.cell}>
            {item.salesValutionStatus === salesValuationTypes.viewValuation ? (
              <Button
                button={{
                  type: ButtonTypes.button,
                  onClick: () => openInfoWindow(item, variant),
                }}
                underline
                variant={ButtonVariants.textLinkSmall}
              >
                {t('viewValuation')}
              </Button>
            ) : (
              <Text size={TextSizes.bodySml}>{item.salesValuation}</Text>
            )}
          </div>
        ),
        guidePrice: (
          <div className={styles.cell}>
            {getArtworkPrice(
              item.artwork.listPriceMin,
              item.artwork.listPriceMax,
              item.artwork.poa
            ) !== 'POA' ? (
              renderGuidePrice(item.guidePrice, activeCurrencyIso || '')
            ) : (
              <Text size={TextSizes.bodySml}>{t('priceDataUnavailable')}</Text>
            )}
          </div>
        ),
        purchaseStatus: (
          <div className={styles.cell}>
            {item.purchaseStatus && (
              <Text size={TextSizes.bodySml}>{item.purchaseStatus}</Text>
            )}
          </div>
        ),
        marketValue: (
          <div className={styles.cell}>
            {item.marketValuationStatus ===
            valuationStatusTypes.viewValuation ? (
              <Button
                button={{
                  type: ButtonTypes.button,
                  onClick: () => openInfoWindow(item, variant),
                }}
                underline
                variant={ButtonVariants.textLinkSmall}
              >
                {t('viewValuation')}
              </Button>
            ) : (
              <Text size={TextSizes.bodySml}>
                {item.artwork.agnostic &&
                  item?.marketValue?.type === 'purchase' && (
                    <>
                      {t('myEstimation')} <br />
                    </>
                  )}
                {renderMarketValue(item.marketValue, activeCurrencyIso || '')}
              </Text>
            )}
          </div>
        ),
        growth5Year: item.growth5Year ? (
          <div
            className={classNames(
              styles.cell,
              styles.actionable,
              styles.auctionData
            )}
            onClick={() => {
              analyticsPusher(analyticsEventTypes.gaEvent, {
                gaEventName: 'Growth AAGR clicked',
              })
              openInfoWindow(item, variant)
            }}
          >
            <GrowthStatus size="medium" growth={+item.growth5Year} />
          </div>
        ) : (
          <GrowthStatus
            size="medium"
            growth={undefined}
            graphGrey
            showInfoIcon
            showEmptyText
          />
        ),
        growth5YearRaw: item.growth5Year,
        canExpand: item.growth5Year ? false : true,
        saleStatus: item.saleStatus && (
          <div className={styles.cell}>
            <Text size={TextSizes.bodySml}>{item.saleStatus}</Text>
          </div>
        ),
        inventoryPriority: (
          <div className={styles.cell}>
            <AvailabilityIndicator
              showTooltip={false}
              inventoryPriority={item.inventoryPriority}
            />
          </div>
        ),
        cta: (
          <div
            className={classNames(styles.cell, styles.ctaCell, {
              [styles.isCardView]: !isTableView,
            })}
          >
            <PortfolioCtas
              type={variant}
              smallFont
              stack={isTableView}
              item={item}
              noMinWidth
              eventSource={variant === 'Watching' ? 'Watchlist' : 'MyPortfolio'}
            />
          </div>
        ),
        tableRowFooter: (
          <div className={styles.portfolioTableItemFooterContainer}>
            <div className={styles.portfolioTableItemFooter}>
              <div className={styles.tradingFloorLozenges}>
                <TicketLozenges
                  buyTicketsCount={item.artwork.buyTicketsCount}
                  sellTicketsCount={item.artwork.sellTicketsCount}
                  ownedTicketsCount={item.artwork.ownedTicketsCount}
                  size={TicketLozengeSizes.small}
                  inline
                  paddingTop={false}
                />
              </div>
              <div className={styles.portfolioTableItemFooterCtas}>
                {variant !== PortfolioCollectionVariant.Selling && (
                  <div className={styles.ctaDelete}>
                    <PortfolioItemDelete
                      item={item}
                      onDelete={() => setDeletedItemIndex(index)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
        hasHistory: item.hasHistory ? (
          <div
            className={classNames(
              styles.cell,
              styles.actionable,
              styles.auctionData
            )}
            onClick={() => openInfoWindow(item, variant)}
          >
            <Icon type="Growth" width={16} height={16} fill="#6844d6" />
            <Text size={TextSizes.primaryTitleXSml}>{t('view')}</Text>
          </div>
        ) : (
          <Text size={TextSizes.bodySml}>{t('notAvailable')}</Text>
        ),
      }
    })

  return (
    <Table<Listing>
      name="portfolio-collection"
      columns={columns}
      noMarginTop
      data={data as any}
      minHeight={200}
      isTableView={isTableView}
      onSort={handleArtworkSort}
      isMyPortfolio
      renderCard={(props: any) => {
        const artwork = props.artworkDetails.props.item.artwork
        return (
          <PortfolioItemCard
            {...props}
            artwork={artwork}
            item={props.artworkDetails.props.item}
            inventoryPriority={props.inventoryPriority}
            variant={variant}
            growth5Year={
              props.growth5YearRaw === null
                ? props.growth5YearRaw
                : props.growth5Year
            }
            hideDelete
          />
        )
      }}
    />
  )
}

export default function PortfolioCollectionTable({
  testID,
  items,
  variant,
  isTableView = true,
}: PortfolioCollectionTableProps) {
  const tCommon = useTranslations('common')
  const isMobile = useMediaQuery(`(max-width: ${bp.bpLargeMax})`)

  const renderMobileView = () => {
    return (
      <Container>
        <div className={classNames(!isTableView && styles.cardsWrap)}>
          {items.map((item, i) => {
            if (!item.artwork) return null
            return (
              <div
                key={`portfolio-collection-item-${i}`}
                className={classNames(styles.portfolioCardWrap, {
                  [styles.isTableItem]: isTableView,
                })}
              >
                {isTableView ? (
                  <PortfolioCard
                    item={item}
                    offwhite={
                      variant !== PortfolioCollectionVariant.Myportfolio
                    }
                    variant={variant}
                  />
                ) : (
                  <PortfolioItemCard
                    artwork={item.artwork}
                    growth5Year={item.growth5Year}
                    inventoryPriority={item.inventoryPriority}
                    item={item}
                    hideDelete
                    variant={variant}
                    cta={
                      <PortfolioCtas
                        type={variant}
                        smallFont
                        stack={isTableView}
                        item={item}
                        noMinWidth
                      />
                    }
                  />
                )}
              </div>
            )
          })}
        </div>
      </Container>
    )
  }

  if (isMobile) return <>{renderMobileView()}</>

  return (
    <div
      className={classNames(styles.base, styles.offwhite, {
        [styles.isCardView]: !isTableView,
      })}
      data-testid={testID}
    >
      <Container>
        <TableView items={items} variant={variant} isTableView={isTableView} />
      </Container>
    </div>
  )
}
